import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "./Guides.css";
import backend from "../../../../../api";

/* MUI */
import {
  Container,
  Chip,
  Breadcrumbs,
  Link,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  Button,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import MosqueRoundedIcon from "@mui/icons-material/MosqueRounded";
import FlightTakeoffRoundedIcon from "@mui/icons-material/FlightTakeoffRounded";

const FALLBACK =
  "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?q=80&w=1200&auto=format&fit=crop";

const badge = (cat) =>
  cat === "ritual" ? (
    <Chip
      icon={<MosqueRoundedIcon />}
      label="Ritual guidance"
      size="small"
      className="gl-badge"
    />
  ) : cat === "pretravel" ? (
    <Chip
      icon={<FlightTakeoffRoundedIcon />}
      label="Pre-travel guide"
      size="small"
      className="gl-badge"
    />
  ) : null;

export default function GuideDetail() {
  const nav = useNavigate();
  const { slug } = useParams();
  const { state } = useLocation();

  const [guide, setGuide] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchGuide = async () => {
    if (!slug) return;
    try {
      setLoading(true);
      const { data } = await backend.get(`/api/content/slug/${slug}`, {
        params: { id: state?.guideId },
      });
      setGuide(data?.data || null);
      setRelated(data?.related || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGuide();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (!guide && !loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h5" color="error" gutterBottom>
          Guide not found
        </Typography>
        <Button onClick={() => nav("/guides")} variant="contained">
          Back to Guides
        </Button>
      </Container>
    );
  }

  return (
    <main className="gd-wrap">
      <Container maxWidth="lg" className="gd-head">
        <Button
          className="gd-back"
          onClick={() => nav(-1)}
          startIcon={<ArrowBackRoundedIcon />}
          aria-label="Go back to previous page"
          variant="text"
        >
          Back
        </Button>

        <Breadcrumbs aria-label="breadcrumb navigation" className="gd-bc">
          <Link
            underline="hover"
            color="inherit"
            onClick={() => nav("/")}
            role="link"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && nav("/")}
          >
            Home
          </Link>
          <Link
            underline="hover"
            color="inherit"
            onClick={() => nav("/guides")}
            role="link"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && nav("/guides")}
          >
            Guides
          </Link>
          <Typography color="text.primary">
            {guide?.title || "Loading..."}
          </Typography>
        </Breadcrumbs>

        <h1 className="gd-title">{guide?.title}</h1>
        {guide && badge(guide.category)}
      </Container>

      {guide && (
        <>
          <div className="gd-banner">
            <img
              src={guide.imageUrl || FALLBACK}
              alt={`Illustration for ${guide.title} guide`}
              onError={(e) => (e.currentTarget.src = FALLBACK)}
              loading="lazy"
            />
            <div className="gd-bannerFade" />
          </div>

          <Container maxWidth="lg" className="gd-body">
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <div className="gd-article">
                  {guide.bodyHtml ? (
                    <div
                      className="gd-article-html"
                      // Content created by admin in ReactQuill
                      dangerouslySetInnerHTML={{ __html: guide.bodyHtml }}
                    />
                  ) : (
                    <Typography color="text.secondary">
                      No content available for this guide.
                    </Typography>
                  )}
                </div>
              </Grid>

              <Grid item xs={12} md={4}>
                <h4 className="gd-sideTitle">Related guides</h4>
                {related.map((r) => (
                  <Card key={r._id} className="gd-rel">
                    <CardActionArea
                      onClick={() =>
                        nav(`/guides/${r.slug}`, {
                          state: { guideId: r._id },
                        })
                      }
                    >
                      <CardMedia
                        component="img"
                        image={r.imageUrl || FALLBACK}
                        onError={(e) => (e.currentTarget.src = FALLBACK)}
                        alt={`Illustration for ${r.title} guide`}
                        loading="lazy"
                      />
                      <CardContent>
                        <Typography
                          variant="subtitle1"
                          className="gl-cardTitle"
                        >
                          {r.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          className="gl-cardExcerpt"
                        >
                          {r.excerpt}
                        </Typography>
                        <Button className="gl-readMore">Read More</Button>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                ))}
                {related.length === 0 && !loading && (
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => nav("/guides")}
                  >
                    View all guides
                  </Button>
                )}
              </Grid>
            </Grid>
          </Container>
        </>
      )}
    </main>
  );
}
